import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import
{
    APP_INITIALIZER,
    ApplicationConfig,
    EnvironmentProviders,
    PLATFORM_ID,
    Provider,
    TransferState,
    importProvidersFrom
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { RouterOutlet, provideRouter } from '@angular/router';
import { LOCK_FEATURE_ENABLED } from "@core/constants/lock-feature";
import { CoreBoot } from "@core/core-boot/core-boot";
import { CoreModule } from '@core/core.module';
import { dbConfig } from "@core/office/office-chat-repository/office-chat-repository";
import { TranslateCompiler, TranslateLoader, TranslateModule } from "@ngx-translate/core";
import { HealthService } from "@services/health/health.service";
import { NgxIndexedDBModule } from "ngx-indexed-db";
import
{
    CLIPBOARD_OPTIONS,
    ClipboardButtonComponent,
    MARKED_OPTIONS,
    MarkdownModule,
    MarkedOptions,
    MarkedRenderer
} from 'ngx-markdown';
import { TranslateMessageFormatCompiler } from "ngx-translate-messageformat-compiler";
import { ApiInterceptor } from './interceptors/api.interceptor';
import { apiSessionInterceptor } from "./interceptors/api-session.interceptor";
import { HttpLoaderFactory } from "./modules/i18n/i18n.module";
import { routes } from './routes/routes';

const indexedDBProviders = NgxIndexedDBModule.forRoot(dbConfig).providers as Array<Provider | EnvironmentProviders>;

function initializeApp(coreBoot: CoreBoot): () => Promise<void>
{
    return () => coreBoot.bootstrap();
}

function probeBackendHealth(health: HealthService): () => void
{
    return () => { void health.refresh(); };
}

/** Markdown links open in a new tab — the addin pane itself must never be navigated away from. */
function markedOptionsFactory(): MarkedOptions
{
    const renderer = new MarkedRenderer();
    const renderLink = renderer.link;

    renderer.link = function (this: MarkedRenderer, href: string, title: string | null | undefined, text: string): string
    {
        const html = renderLink.call(this, href, title, text);
        // marked returns plain text (no anchor) for hrefs it refuses to link.
        return html.replace(/^<a /, '<a target="_blank" rel="noopener noreferrer" ');
    };

    return { renderer };
}


export const appConfig: ApplicationConfig = {
    providers: [
        importProvidersFrom(
            CommonModule,
            CoreModule,
            RouterOutlet,
            MarkdownModule.forRoot({
                clipboardOptions: {
                    provide: CLIPBOARD_OPTIONS,
                    useValue: {
                        buttonComponent: ClipboardButtonComponent,
                    },
                },
                markedOptions: {
                    provide: MARKED_OPTIONS,
                    useFactory: markedOptionsFactory,
                },
            }),
            TranslateModule.forRoot({
                loader: {
                    provide: TranslateLoader,
                    useFactory: (HttpLoaderFactory),
                    deps: [ HttpClient, TransferState, PLATFORM_ID ]
                },
                compiler: {
                    provide: TranslateCompiler,
                    useClass: TranslateMessageFormatCompiler
                }
            }),
        ),
        provideHttpClient(
            withInterceptors([ apiSessionInterceptor ])
        ),
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ApiInterceptor,
            multi: true
        },
        {
            provide: APP_INITIALIZER,
            useFactory: initializeApp,
            multi: true,
            deps: [ CoreBoot ],
        },
        {
            provide: APP_INITIALIZER,
            useFactory: probeBackendHealth,
            multi: true,
            deps: [ HealthService ],
        },
        { provide: LOCK_FEATURE_ENABLED, useFactory: () => false },
        provideRouter(routes),
        provideAnimations(),
        indexedDBProviders
    ],
};
